import { useCallback, useEffect, useRef, useState } from 'react';
import { signatureRequest } from '../models/signatureApi';
import { mailError } from '../mailboxModel';
import { signatureSaveBody } from '../signatureModel';

export function useSignatureSettings({ userId, can }) {
  const allowed = can(userId ? 'users.workspace_mailbox.settings.manage' : 'mailbox.view');
  const editable = can(userId ? 'users.workspace_mailbox.settings.manage' : 'mailbox.settings.manage');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const flight = useRef(false);
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflictDraft, setConflictDraft] = useState(null);
  const generation = useRef(0);
  const request = useCallback(options => signatureRequest(userId, options), [userId]);
  const apply = data => {
    const saved = data.signature;
    setCurrent(saved); setIdentities(data.send_as_identities || []);
    const next = saved || { send_as_email: data.send_as_identities?.find(row => row.is_primary)?.send_as_email || '', signature_html: '', enabled: true, apply_to_new_messages: true, apply_to_replies: true, sync_to_gmail: true };
    setForm(next); setBaseline(next);
  };
  const load = useCallback(async () => {
    if (!allowed) return;
    const ticket = ++generation.current;
    setLoading(true); setError('');
    try { const data = await request(); if (ticket === generation.current) apply(data); }
    catch (failure) { if (ticket === generation.current) setError(mailError(failure)); }
    finally { if (ticket === generation.current) setLoading(false); }
  }, [allowed, request]);
  useEffect(() => {
    const requests = generation;
    load();
    return () => { requests.current++; };
  }, [load]);
  const save = async () => {
    if (!editable || flight.current) return false;
    if (new Blob([form.signature_html]).size > 100000) { setError('Signature HTML must be no more than 100 KB.'); return false; }
    flight.current = true;
    const ticket = generation.current;
    setSaving(true); setError(''); setNotice('');
    try {
      const saved = await request({ method: 'put', data: signatureSaveBody(form, current) });
      if (ticket !== generation.current) return false;
      setCurrent(saved); setForm(saved); setBaseline(saved); setConflictDraft(null);
      setNotice('Signature saved in Uzdpart360.');
      return true;
    } catch (failure) {
      if (ticket !== generation.current) return false;
      if (failure.response?.status === 409 && failure.response?.data?.data?.code === 'MAIL_SIGNATURE_VERSION_CONFLICT') {
        setConflictDraft(form);
        setNotice('Someone updated this signature. The latest setting is loaded below. Review it before saving again. Your attempted HTML is retained below for reference.');
        setForm(null);
        await load();
      } else setError(mailError(failure));
      return false;
    } finally { flight.current = false; setSaving(false); }
  };
  const change = values => setForm(previous => ({ ...previous, ...values }));
  return { allowed, editable, current, form, identities, loading, saving, error, notice, conflictDraft, load, save, change, dirty: !!form && JSON.stringify(form) !== JSON.stringify(baseline) };
}
